/**************************************
 * [COMPONENT]: L-DEFERRED-MEDIA
 ***************************************/
class DeferredMedia extends HTMLElement {
	constructor() {
		super()
		const poster = this.querySelector('[data-trigger]')

		if (!poster) return
		poster.addEventListener('click', this.loadContent.bind(this))
	}

	loadContent(focus = true) {
		if (!this.getAttribute('loaded')) {
			const content = document.createElement('div')
			content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true))

			this.setAttribute('loaded', true)
			const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'))
			if (focus) deferredElement.focus()

			if (deferredElement.nodeName == 'IFRAME') {
				deferredElement.src += '&autoplay=1'
			}

			if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
				// force autoplay for safari
				deferredElement.play()
			}
		}
	}
}

customElements.define('deferred-media', DeferredMedia)

/**************************************
 * [COMPONENT]: L-PRODUCT-ADDED-TO-CART
 ***************************************/
if (!customElements.get('l-product-added-to-cart')) {
	customElements.define(
		'l-product-added-to-cart',
		class LTAProductAddedToCartMessage extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.availableOnMarkets = this.dataset.availableOnMarkets || 'US'

				// Setup events
				this.checkLocalizationIP()

				document.addEventListener('l:cart:updated', (event) => {
					this.classList.toggle('is-active', this.availableOnMarkets.includes(this.currentCountryISOCode))
				})
			}

			checkLocalizationIP() {
				fetch(
					window.Shopify.routes.root +
						'browsing_context_suggestions.json' +
						'?country[enabled]=true' +
						`&country[exclude]=${window.Shopify.country}` +
						'&language[enabled]=true' +
						`&language[exclude]=${window.Shopify.language}`
				)
					.then((r) => r.json())
					.then((data) => {
						const countryISOCode = data.detected_values.country.handle
						this.currentCountryISOCode = countryISOCode
					})
					.catch((error) => {
						// console.error(error)
					})
			}
		}
	)
}

/**************************************
 * [COMPONENT]: L-DELIVERY-INFO
 ***************************************/
class LTADeliveryInfo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.textEl = this.querySelector('[data-text]')

		// Data
		this.restockMin = parseInt(this.dataset.restockMin)
		this.restockMax = parseInt(this.dataset.restockMax)
		this.minRestock = parseInt(this.dataset.min)
		this.maxRestock = parseInt(this.dataset.max)

		// Event
		this.initDate(true)

		document.addEventListener('variantUpdate', (event) => {
			const isPreOrder = event.detail?.isPreorder
			const available = event.detail?.available

			this.initDate(isPreOrder)
		})
	}

	initDate(isPreOrder) {
		let today = new Date()
		let baseDateMin = today
		let baseDateMax = today

		if (isPreOrder) {
			baseDateMin = new Date(today.getTime() + this.restockMin * 24 * 60 * 60 * 1000)
			baseDateMax = new Date(today.getTime() + this.restockMax * 24 * 60 * 60 * 1000)
		}

		const minDate = new Date(baseDateMin.getTime() + this.minRestock * 24 * 60 * 60 * 1000)
		const maxDate = new Date(baseDateMax.getTime() + this.maxRestock * 24 * 60 * 60 * 1000)

		const dayMin = minDate.getDate()
		const dayMax = maxDate.getDate()

		const monthMin = minDate.toLocaleString('en-US', { month: 'short' })
		const monthMax = maxDate.toLocaleString('en-US', { month: 'short' })

		const backOrderedText = isPreOrder ? 'Backordered. ' : ''
		const deliveryText =
			this.minRestock !== this.maxRestock || baseDateMin.getTime() !== baseDateMax.getTime()
				? `<span>${monthMin} ${dayMin}</span> - <span>${monthMax !== monthMin ? monthMax + ' ' : ''}${dayMax}</span>`
				: `<span>${monthMin} ${dayMin}</span>`

		this.textEl.innerHTML = `${backOrderedText}Get it by ${deliveryText}`
	}
}

customElements.define('l-delivery-info', LTADeliveryInfo)

/**************************************
 * [COMPONENT]: L-PRODUCT-CONTROLS
 ***************************************/
if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LTAProductControls extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Global elements
				this.productFormEl = document.querySelector('[id^="product-form-')

				// Elements
				this.atcButtonEl = this.querySelector('[data-pdp-page-atc-form]')
				this.priceEl = this.querySelector('[data-product-price]')
				this.backOrderTagEl = this.querySelector('[data-backorder-delivery-estimation]')
				this.availableTagEl = this.querySelector('[data-delivery-estimation]')
				this.outOfStockTagEl = this.querySelector('[data-out-of-stock-tag]')

				// Label
				this.atcLabel = this.atcButtonEl.dataset.atcLabel
				this.soldOutLabel = this.atcButtonEl.dataset.soldOutLabel
				this.preOrderLabel = this.atcButtonEl.dataset.preOrderLabel

				// Events
				this.handleSubmitEvent()
				this.handleOnVariantChanged()
				this.getVariantsData()

				document.addEventListener('cart:update:count', (event) => {
					l_debounce(() => {
						this.atcButtonEl.classList.remove('is-loading')
					}, 1000)()
				})
			}

			handleSubmitEvent() {
				this.atcButtonEl.addEventListener('click', (event) => {
					// Customized products
					const submitATCEl = this.productFormEl.querySelector('.mw-apo-add-to-cart-cloned')
					if (submitATCEl) {
						submitATCEl.click()
						submitATCEl.dispatchEvent(new Event('click', { bubbles: true }))

						return
					}

					// Normal products
					this.atcButtonEl.classList.add('is-loading')
				})
			}

			handleOnVariantChanged() {
				document.addEventListener('l:variant:changed', (event) => {
					const variant = event.detail.currentVariant
					const { id, price, isPreOrder, isOutOfStock, isAvailable } = this.variantData.find(
						(_v) => _v.id === variant.id
					)

					// Update ATC button
					this.atcButtonEl.toggleAttribute('disabled', !isAvailable)
					this.atcButtonEl.innerHTML = isOutOfStock
						? this.soldOutLabel
						: isPreOrder
							? this.preOrderLabel
							: this.atcLabel

					// Update price
					if (this.priceEl) this.priceEl.innerHTML = price

					if (this.availableTagEl) {
						this.availableTagEl.classList.toggle('is-active', isAvailable)
						this.backOrderTagEl.classList.toggle('is-active', isPreOrder)
						this.outOfStockTagEl.classList.toggle('is-active', isOutOfStock)
					}
				})
			}

			getVariantsData() {
				this.variantData = JSON.parse(this.querySelector('[data-variants-data]').textContent)

				return this.variantData
			}
		}
	)
}

/**************************************
 * [COMPONENT]: L-ESTIMATION-DELIVERY
 ***************************************/
class LTAEstimationDelivery extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.blockEls = this.querySelectorAll('[data-block]')

		// Events
		this.setupEstimationDelivery()

		document.addEventListener('l:variant:changed', (event) => {
			const variant = event.detail.currentVariant
			const isPreOrder = variant.available && variant.inventory_quantity <= 0

			this.setupEstimationDelivery(isPreOrder)
		})
	}

	setupEstimationDelivery(isPreOrder = false) {
		const today = new Date()
		const minRestock = parseInt(this.querySelector('[data-block="restock"]').dataset.min)
		const maxRestock = parseInt(this.querySelector('[data-block="restock"]').dataset.max)

		this.blockEls.forEach((blockEl) => {
			const dataEl = blockEl.querySelector('[data-date]')
			const blockType = blockEl.getAttribute('data-block')

			// Special case for data-order-placed
			if (blockType == 'order-placed') {
				const day = today.getDate()
				const month = today.toLocaleString('en-US', { month: 'short' })
				dataEl.innerHTML = `${month} ${day}`

				return
			}

			// Toggle elements
			if (isPreOrder) {
				if (blockType == 'restock') blockEl.classList.remove('t4s-d-none')
				if (blockType == 'order-placed') blockEl.classList.add('t4s-d-none')
			} else {
				if (blockType == 'restock') blockEl.classList.add('t4s-d-none')
				if (blockType == 'order-placed') blockEl.classList.remove('t4s-d-none')
			}

			// Setup estimation date

			let baseDateMin, baseDateMax

			const min = parseInt(blockEl.dataset.min)
			const max = parseInt(blockEl.dataset.max)

			if (isPreOrder) {
				if (blockType == 'restock') {
					// For restock item when isPreorder is true
					baseDateMin = today
					baseDateMax = today
				} else {
					// For all other items when isPreorder is true, use maxRestock as base
					baseDateMin = new Date(today.getTime() + minRestock * 24 * 60 * 60 * 1000)
					baseDateMax = new Date(today.getTime() + maxRestock * 24 * 60 * 60 * 1000)
				}
			} else {
				baseDateMin = today
				baseDateMax = today
			}

			const minDate = new Date(baseDateMin.getTime() + min * 24 * 60 * 60 * 1000)
			const maxDate = new Date(baseDateMax.getTime() + max * 24 * 60 * 60 * 1000)

			const dayMin = minDate.getDate()
			const dayMax = maxDate.getDate()

			const monthMin = minDate.toLocaleString('en-US', { month: 'short' })
			const monthMax = maxDate.toLocaleString('en-US', { month: 'short' })

			dataEl.innerHTML =
				min !== max || baseDateMin.getTime() !== baseDateMax.getTime()
					? `<span>${monthMin} ${dayMin}</span> - <span>${monthMax !== monthMin ? monthMax + ' ' : ''}${dayMax}</span>`
					: `<span>${monthMin} ${dayMin}</span>`
		})
	}
}

customElements.define('l-product-estimation', LTAEstimationDelivery)

/**************************************
 * [COMPONENT]: L-PRODUCT-INSTALLMENTS
 ***************************************/
if (!customElements.get('l-product-installments')) {
	customElements.define(
		'l-product-installments',
		class LTAProductsInstallments extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Subscribe global event
				document.addEventListener('l:variant:changed', (event) => {
					const variantId = event.detail.currentVariant.id

					const input = this.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}
		}
	)
}

/**************************************
 * [COMPONENTS]: L-PRODUCT-MODAL-GALLERY
 ***************************************/
if (!customElements.get('l-product-modal-gallery')) {
	customElements.define(
		'l-product-modal-gallery',
		class LTAProductModalGallery extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.closeButtonEls = this.querySelectorAll('[data-close]')
				this.cardEl = this.querySelector('[data-card]')
				this.mediaEls = this.querySelectorAll('[data-media]')

				// Events
				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})

				this.closeButtonEls.forEach((closeButtonEl) => {
					closeButtonEl.addEventListener('click', (event) => {
						this.close()
					})
				})

				document.addEventListener('media:click', (event) => {
					// Open the active media
					const mediaId = event.detail.mediaId
					this.mediaEls.forEach((mediaEl, index0) => {
						const _mediaId = mediaEl.getAttribute('data-media-id')
						if (_mediaId == mediaId) {
							this.mainCarouselSwiper.slideTo(index0)
						}
					})

					// Open the modal
					this.open()
				})

				this.addEventListener('click', this.outSideClick.bind(this))
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					navigation: {
						nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
						prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
					},
				})

				// Setup carousel for main media
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				// Setup controller
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
			}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = swiper.slides[swiper.activeIndex]

					l_pauseAllMedia(this)
					l_playMedia(activeSlideEl)
				})
			}

			open() {
				this.classList.add('is-active')
			}

			close() {
				this.classList.remove('is-active')
				l_pauseAllMedia(this)
			}

			outSideClick(event) {
				if (!this.cardEl.contains(event.target) && this.classList.contains('is-active')) {
					this.close()
				}
			}
		}
	)
}

if (!customElements.get('l-media-zoom')) {
	customElements.define(
		'l-media-zoom',
		class LTAMediaZoom extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				document.addEventListener('zoomifyJs:loaded', () => {
					this.setupZoomMedia()
				})
			}

			setupZoomMedia() {
				const zoomify = new ZoomifyJs({
					selector: this.querySelector('.js-zoomifyJs-image'),
					transitionDuration: 300,
					easing: 'ease-in-out',
					scale: 2,
					clickToZoom: true,
				})
			}
		}
	)
}

if (!customElements.get('l-product-media-single')) {
	customElements.define(
		'l-product-media-single',
		class LTAMediaSingle extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('click', (event) => {
					this.dispatchEvent(
						new CustomEvent('media:click', { bubbles: true, detail: { mediaId: this.dataset.mediaId } })
					)
				})
			}
		}
	)
}

if (!customElements.get('l-external-media')) {
	class LTAProductModalExternalMedia extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this.containerEl = this.querySelector('[data-media-container]')
			this.triggerButtonEl = this.querySelector('[data-trigger-button]')
			this.templateEl = this.querySelector('template')

			// Data
			this.isPlaying = false

			// Events
			if (this.triggerButtonEl) {
				this.triggerButtonEl.addEventListener('click', () => {
					this.isPlaying ? this.pauseVideo() : this.playVideo()
				})
			}
		}

		playVideo() {
			if (this.triggerButtonEl) {
				this.triggerButtonEl.classList.add('is-loading')
				l_debounce(() => {
					this.triggerButtonEl.classList.remove('is-loading')
				}, 1000)()
			}

			this.templateContentHTML = this.templateEl.content.cloneNode(true)
			this.containerEl.appendChild(this.templateContentHTML)

			this.playVideoTag()

			this.isPlaying = true
		}

		pauseVideo() {
			this.containerEl.innerHTML = ''
			this.isPlaying = false
		}

		playVideoTag() {
			const videoTag = this.containerEl.querySelector('video')

			if (videoTag) videoTag.play()
		}
	}

	customElements.define('l-external-media', LTAProductModalExternalMedia)
}

/**************************************
 * [COMPONENT]: VARIANT SELECTOR
 ***************************************/
class LTAProductVariantSelector extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		// Event
		document.addEventListener('l:variant:changed', (event) => {
			const variant = event.detail.currentVariant
			const url = `${this.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.sectionId}`

			this.renderProductVariantSelector(url)
		})
	}

	renderProductVariantSelector(url) {
		fetch(url)
			.then((response) => response.text())
			.then((html) => {
				const productSelectEl = new DOMParser()
					.parseFromString(html, 'text/html')
					.querySelector('#ProductVariantSelector')

				const newSelectWrapperEls = productSelectEl.querySelectorAll('[data-swatch-option]')

				newSelectWrapperEls.forEach((newSelectWrapperEl) => {
					// Wrapper element
					const id = newSelectWrapperEl.id
					const currentSelectWrapperEl = this.querySelector(
						`[data-swatch-option="${newSelectWrapperEl.dataset.swatchOption}"]`
					)

					// Update data-dropdown-open
					const newCurrentValueEl = newSelectWrapperEl.querySelector('[data-dropdown-open]')
					const currentValueEl = currentSelectWrapperEl.querySelector('[data-dropdown-open]')

					if (newCurrentValueEl && currentValueEl) {
						currentValueEl.innerHTML = newCurrentValueEl.innerHTML
					}

					// Update list
					const newSelectItemEls = Array.from(newSelectWrapperEl.querySelectorAll('[data-swatch-item]'))
					const oldSelectItemEls = Array.from(currentSelectWrapperEl.querySelectorAll('[data-swatch-item]'))

					oldSelectItemEls.forEach((element, index0) => {
						if (element && newSelectItemEls[index0]) {
							element.innerHTML = newSelectItemEls[index0].innerHTML
						}
					})
				})
			})
	}
}

customElements.define('l-product-variant-selector', LTAProductVariantSelector)

/**************************************
 * [MODULE]: PRODUCT ADD TO CART MODAL
 ***************************************/

if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.productFormEl = this.querySelector('l-product-form')

				this.selectedSellingPlanId = this.dataset.sellingPlanId
				this.productId = this.dataset.productId

				this.initializeProductSwapUtility()
				this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))
				this.setupEventListeners()
			}

			setupEventListeners() {
				// Variant option changes
				document.addEventListener('l:variant:option-changed', (event) => {
					const { target, selectedOptionValues, productId, sectionId } = event.detail

					if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return

					this.handleOptionValueChange(target, selectedOptionValues, null)
				})

				// Selling plan changes [...]

				// Product Added: This is used to update the product info when a product is added to the cart [...]

				// Cart updates [...]
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					// Re-initialize Shopify functionality after DOM updates
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange(target, selectedOptionValues, variant) {
				// Reset form state
				this.resetProductFormState()

				const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url
				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
					targetId: target?.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				if (this.productFormEl) {
					this.productFormEl.global_toggleSubmitButton(true, null, false)
					this.productFormEl.handleErrorMessage()
				}
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				// Cancel any pending requests
				this.abortController?.abort()
				this.abortController = new AbortController()
				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')
						callback(html)
					})
					.then(() => {
						// Focus the element that triggered the update
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						// Only log actual errors, not aborted requests
						if (error.name !== 'AbortError') {
							console.error('Product info fetch error:', error)
						}
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					// Update URL with new variant ID
					this.updateURL(productUrl, variant?.id)
					if (updateFullPage) {
						// Update page title
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML
						// Swap the entire main content
						l_swapHtmlNode(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						// Swap just the product info section
						l_swapHtmlNode(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					// Update DOM elements
					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					// Handle unavailable variant
					if (!variant) {
						document.dispatchEvent(
							new CustomEvent('l:variant-v2:changed', {
								detail: { sectionId: this.sectionId, html, productId: this.dataset.productId, variant },
							})
						)

						return
					}

					// Update product sections from HTML response
					this.updateProductSections(html)
					// Notify about variant change
					document.dispatchEvent(
						new CustomEvent('l:variant-v2:changed', {
							detail: { sectionId: this.sectionId, html, productId: this.dataset.productId, variant },
						})
					)
				}
			}

			updateProductSections(html) {
				const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = () => false) => {
					const source = html.getElementById(`${id}-${this.sectionId}`)
					const destination = document.getElementById(`${id}-${this.dataset.section}`)
					if (source && destination) {
						destination[htmlMethod] = source[htmlMethod]
						destination.classList.toggle('l-hidden', shouldHide(source))
					}
				}

				// Update product sections
				// updateSourceFromDestination('stockInfo', 'outerHTML')
				// updateSourceFromDestination('stickyATC', 'outerHTML')

				updateSourceFromDestination('prices')
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')
				if (variantSelects) {
					l_swapHtmlNode(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				const currentUrlParams = new URLSearchParams(window.location.search)
				const params = new URLSearchParams()

				if (variantId) params.append('variant', variantId)

				if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)

				const viewTemplate = currentUrlParams.get('view')
				if (viewTemplate) params.append('view', viewTemplate)

				const newUrl = `${url}?${params.toString()}`

				window.history.replaceState({}, '', newUrl)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML

				return selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
				const params = []

				// Add section ID if not fetching full page
				if (!shouldFetchFullPage) {
					params.push(`section_id=${this.sectionId}`)
				}

				// Add selling plan if selected
				if (this.selectedSellingPlanId) {
					params.push(`selling_plan=${this.selectedSellingPlanId}`)
				}

				// Add option values or variant ID
				if (optionValues?.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				} else if (variant) {
					params.push(`variant=${variant.id}`)
				}

				return `${url}?${params.join('&')}`
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	class LTAVariantSelect extends HTMLElement {
		constructor() {
			super()

			this.onChange = this.onChange.bind(this)
		}

		connectedCallback() {
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			this.addEventListener('change', this.onChange)
		}

		onChange(event) {
			const target = this._getInputFromEvent(event.target)
			this._updateSelectionMetadata(target)

			document.dispatchEvent(
				new CustomEvent('l:variant:option-changed', {
					detail: {
						event,
						target,
						selectedOptionValues: this.selectedOptionValues,
						productId: this.productId,
						sectionId: this.sectionId,
					},
				})
			)
		}

		_updateSelectionMetadata(target) {
			const { value, tagName } = target

			// Skip for select elements with selected options
			if (tagName === 'SELECT' && target.selectedOptions.length) return

			// Update selected value display for swatch inputs
			if (target.hasAttribute('data-product-variant-input')) {
				const optionsContainer = target.closest('[data-variant-options]')
				if (!optionsContainer) return
				const selectedSwatchValue = optionsContainer.querySelector('[data-selected-value]')
				if (selectedSwatchValue) {
					selectedSwatchValue.innerHTML = value
				}
			}
		}

		_getInputFromEvent(target) {
			return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
		}

		get selectedOptionValues() {
			return Array.from(
				this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
			).map((el) => el.dataset.optionValueId)
		}
	}

	customElements.define('l-variant-selects', LTAVariantSelect)
}

if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LTAProductForm extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Cache DOM elements
				this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
				this.cart = document.getElementById('CartDrawer')
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
				this.dynamicCheckoutEl = this.querySelector('[data-dynamic-checkout]')
				// this.backInStockButtonEl = this.querySelector('[data-open-back-in-stock-trigger]')

				// Store section data
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId
				this.selectedVariantId = this.dataset.selectedVariantId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Initialize event listeners
				if (this.form) {
					this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
				}

				document.addEventListener('l:variant-v2:changed', this.handleOnVariantChange.bind(this))
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant-v2:changed', this.handleOnVariantChange)
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId, variant } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				// Update back in stock button
				// if (this.backInStockButtonEl) {
				// 	this.backInStockButtonEl.classList.toggle('l-hidden', !variant || (variant && variant.available))
				// }

				// Hide items if variant is not available
				if (this.dynamicCheckoutEl) {
					this.dynamicCheckoutEl.classList.toggle('l-hidden', !variant)
				}

				// Hide submit button if variant is not exists
				if (!variant) {
					this.global_toggleSubmitButton(true, 'unavailable')

					return
				}

				// Replace submit button
				const newSubmitButtonEl = html.getElementById(`ProductSubmitButton-${this.sectionId}`)

				if (!newSubmitButtonEl) return

				// const priceEl = newSubmitButtonEl.querySelector('[data-product-normal-price]')
				// if (priceEl) priceEl.innerHTML = ''

				this.submitButtonEl.replaceWith(newSubmitButtonEl)

				// Re-query elements
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				// Don't submit if button is disabled
				if (this.submitButtonEl.getAttribute('aria-disabled') === 'true') return

				// Set button to loading state
				this.submitButtonEl.setAttribute('aria-disabled', true)
				this.submitButtonEl.classList.add('is-loading')

				// Create cart configuration and add to cart
				const config = ltaCartService.createConfigFromForm(this.form)

				ltaCartService
					.addToCart(config, 'l-product-form')
					.then((response) => {
						if (response?.status) this.handleErrorMessage(response.description)
					})
					.catch((e) => {
						console.error('Add to cart error:', e)
					})
					.finally(() => {
						// Remove loading state
						this.submitButtonEl.classList.remove('is-loading')
						if (!this.error) this.submitButtonEl.removeAttribute('aria-disabled')

						l_debounce(() => this.handleErrorMessage.bind(this, false), 1000)()
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				// Find error message elements if not already cached
				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper?.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				// Toggle error message visibility
				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			global_toggleSubmitButton(disable = true, textType, needToUpdateText = true) {
				if (disable) {
					this.submitButtonEl.setAttribute('disabled', 'disabled')
					if (textType && needToUpdateText) this.submitButtonTextEl.textContent = this.getButtonLabel(textType)

					return
				}

				this.submitButtonEl.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonTextEl.textContent = this.getButtonLabel(textType)
				}
			}

			getButtonLabel(textType = 'addToCart') {
				switch (textType) {
					case 'addToCart':
						return this.dataset.atcLabel || window.LTATheme.variantStrings.addToCart
					case 'soldOut':
						return this.dataset.soldOutLabel || window.LTATheme.variantStrings.soldOut
					case 'preOrder':
						return this.dataset.preOrderLabel || window.LTATheme.variantStrings.preOrder
					case 'unavailable':
						return this.dataset.unavailableLabel || window.LTATheme.variantStrings.unavailable
					default:
						return window.LTATheme.variantStrings.addToCart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

/**************************************
 * [SECTION]: L-PRODUCT-STICKY-TAB
 ***************************************/
if (!customElements.get('l-product-sticky-tab')) {
	class LTAProductStickyTab extends HTMLElement {
		constructor() {
			super()

			this._bindMethods()
		}

		_bindMethods() {
			this._onTriggerClick = this._onTriggerClick.bind(this)
			this._onDocumentClick = this._onDocumentClick.bind(this)
			this._onScroll = l_debounce(this._handleStickyState.bind(this), 50)
		}

		connectedCallback() {
			this._cacheDOM()

			if (!this.dropdownMenuEl || !this.triggerDropdownButtonEl) return
			this._addEventListeners()
		}

		disconnectedCallback() {
			this._removeEventListeners()
		}

		_cacheDOM() {
			this.dropdownMenuEl = this.querySelector('[data-dropdown-menu]')
			this.triggerDropdownButtonEl = this.querySelector('[data-trigger-dropdown-button]')
		}

		_addEventListeners() {
			this._handleStickyState()

			this.triggerDropdownButtonEl.addEventListener('click', this._onTriggerClick)

			document.addEventListener('click', this._onDocumentClick)
			document.addEventListener('scroll', this._onScroll)
		}

		_removeEventListeners() {
			if (this.triggerDropdownButtonEl) {
				this.triggerDropdownButtonEl.removeEventListener('click', this._onTriggerClick)
			}
			document.removeEventListener('click', this._onDocumentClick)
			document.removeEventListener('scroll', this._onScroll)
		}

		_handleStickyState() {
			const mainSubmitEl = document.getElementById('l-product-controls')

			if (!mainSubmitEl) return

			const rect = mainSubmitEl.getBoundingClientRect()
			const scrollY = window.scrollY || document.documentElement.scrollTop
			const mainSubmitOffsetTop = scrollY + rect.top
			const isAtTop = scrollY < mainSubmitOffsetTop
			const isMainSubmitInViewport = rect.top < window.innerHeight && rect.bottom > 0
			const isStickyVisible = !isAtTop && !isMainSubmitInViewport

			this.classList.toggle('is-sticky', isStickyVisible)
		}

		_onTriggerClick(event) {
			event.stopPropagation()
			this.dropdownMenuEl.classList.toggle('is-active')
			this.triggerDropdownButtonEl.classList.toggle('is-active')
		}

		_onDocumentClick(event) {
			if (!this.triggerDropdownButtonEl.contains(event.target) && !this.dropdownMenuEl.contains(event.target)) {
				this.dropdownMenuEl.classList.remove('is-active')
				this.triggerDropdownButtonEl.classList.remove('is-active')
			}
		}
	}

	customElements.define('l-product-sticky-tab', LTAProductStickyTab)
}

/**************************************
 * [SECTION]: L-PRODUCT-BB803-MEDIA
 ***************************************/
class LTAProductBB803Media extends customElements.get('l-external-media') {
	connectedCallback() {
		super.connectedCallback()

		this.isAutoPlay = this.dataset.autoPlay == 'true'
		this.isAutoPlay && this.autoPlayVideo()
	}

	autoPlayVideo() {
		const videoTag = this.querySelector('video')

		if (videoTag) videoTag.play()
	}
}

customElements.define('l-external-media-bb803', LTAProductBB803Media)

if (!customElements.get('l-sticky-customize-button')) {
	class LTAStickyCustomizeButton extends HTMLElement {
		constructor() {
			super()

			this._boundHandleVisitedBefore = this._handleVisitedBefore.bind(this)
			this._boundOnClick = this._onClick.bind(this)
			this._boundOnScroll = this._onScroll.bind(this)
		}

		connectedCallback() {
			// Handle visited state if available, otherwise wait for the event
			if (typeof window.isReturningCustomer === 'boolean') {
				this._handleVisitedBefore(window.isReturningCustomer)
			} else {
				document.addEventListener('l:hasVisitedBefore', () => {
					this._handleVisitedBefore(window.isReturningCustomer)
				})
			}

			this._updateVisibility()
			this.addEventListener('click', this._boundOnClick)
			window.addEventListener('scroll', this._boundOnScroll)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this._boundOnClick)
			window.removeEventListener('scroll', this._boundOnScroll)
			document.removeEventListener('l:hasVisitedBefore', this._boundHandleVisitedBefore)
		}

		_handleVisitedBefore(isReturningCustomer) {
			this.classList.toggle('l-hidden', !isReturningCustomer)
		}

		_onClick() {
			const customizeOptionEl = document.querySelector('.mw-apo-container')
			if (customizeOptionEl?.scrollIntoView) {
				customizeOptionEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
			}
		}

		_onScroll() {
			this._updateVisibility()
		}

		_updateVisibility() {
			const infoWrapperEl = document.getElementById('product-info-wrapper')
			const customizedOptionsEl = infoWrapperEl?.querySelector('.mw-apo-container')

			if (!customizedOptionsEl) {
				this.classList.add('is-visible')

				return
			}

			const scrollY = window.scrollY ?? window.pageYOffset
			const rect = customizedOptionsEl.getBoundingClientRect()

			const bottomTriggerPoint = rect.bottom + customizedOptionsEl.scrollHeight
			const topTriggerPoint = rect.top

			const showButton = scrollY >= bottomTriggerPoint || scrollY <= topTriggerPoint

			this.classList.toggle('is-visible', showButton)
		}
	}

	customElements.define('l-sticky-customize-button', LTAStickyCustomizeButton)
}

/**************************************
 * [FUNCTION]: Remove the default option from hand option in product page
 ***************************************/
document.addEventListener('DOMContentLoaded', () => {
	// Debounce utility function
	const debounce = (fn, wait) => {
		let timeout
		return (...args) => {
			clearTimeout(timeout)
			timeout = setTimeout(() => fn.apply(this, args), wait)
		}
	}

	// Setup hand options for PDP
	const setupPDPRightLeftOptions = () => {
		// Check if the current URL has the 'auto_select_variant' search param, if so, return and do nothing
		const urlParams = new URLSearchParams(window.location.search)
		const autoSelectVariant = urlParams.has('auto_select_variant')

		// Setup hand options for PDP
		const handRowEls = document.querySelectorAll('[data-swatch-option="hand"]')

		handRowEls.forEach((handRowEl) => {
			const swatchItemEls = handRowEl.querySelectorAll('[data-swatch-item]')

			swatchItemEls.forEach((swatchItemEl) => {
				if (autoSelectVariant) {
					handRowEl.classList.remove('is-hand-default')
				} else {
					swatchItemEl.addEventListener('click', () => {
						handRowEl.classList.remove('is-hand-default')
					})
				}
			})
		})
	}

	// Initialize hand options setup
	setupPDPRightLeftOptions()

	// Reinitialize on quick view open
	document.addEventListener('l:quickview:open', () => {
		debounce(setupPDPRightLeftOptions, 200)()
	})
})

/**************************************
 * [FUNCTION]: preselect options in customized products
 ***************************************/
document.addEventListener('DOMContentLoaded', (e) => {
	// Variables
	let isLoaded = false

	// Utility functions
	function debounce(fn, wait) {
		let t
		return (...args) => {
			clearTimeout(t)
			t = setTimeout(() => fn.apply(this, args), wait)
		}
	}

	function handlelize(str) {
		str = str
			.normalize('NFD')
			.replace(/[\[\]'()"]+/g, '') // Remove apostrophes, square brackets, and other bits and pieces
			.replace(/[\u0300-\u036f]/g, '') // Remove accents
			.replace(/([^\w]+|\s+)/g, '-') // Replace space and other characters by hyphen
			.replace(/\-\-+/g, '-') // Replaces multiple hyphens by one hyphen
			.replace(/(^-+|-+$)/g, '') // Remove extra hyphens from beginning or end of the string
			.toLowerCase() // To lowercase

		return str
	}

	function clearOptionsName(optionName) {
		let optionNameHandlize = handlelize(optionName)
		const currency = `${Shopify.currency.active}`.toLowerCase()

		if (optionNameHandlize.includes(currency)) {
			const price = '-' + optionNameHandlize.split('-').slice(-3).join('-')
			optionNameHandlize = optionNameHandlize.replace(price, '')
		}

		return optionNameHandlize
	}

	function preselectCustomizedProductsOptions() {
		// Check elements
		const optionRowEls = document.querySelectorAll('.mw-option')
		if (optionRowEls.length == 0 || isLoaded) return

		isLoaded = true

		// Handle param URl
		const queryString = window.location.search
		const urlParams = new URLSearchParams(queryString)

		let shaftMaterial = ''
		let selectEls = []

		optionRowEls.forEach((optionRowEl) => {
			const label = optionRowEl.querySelector('.mw-label .mw-text')
			if (!label) return

			const handlizedLabel = handlelize(label.innerHTML)

			// Check existing query params
			const paramValue = urlParams.get(handlizedLabel)

			// Save shaftMaterial
			if (handlizedLabel == 'shaft-material') {
				shaftMaterial = paramValue
			}

			// Handle based on type: radio
			if (optionRowEl.classList.contains('mw-option--type-radio')) {
				const optionValueEls = optionRowEl.querySelectorAll('.mw-option__container .mw-option__value')

				optionValueEls.forEach((optionValueEl) => {
					const inputEl = optionValueEl.querySelector('input.mw-option__control')
					const optionValueTextEl = optionValueEl.querySelector('.mw-option__value-text')

					if (optionValueTextEl && inputEl) {
						const optionValueTextHandlize = clearOptionsName(optionValueTextEl.innerHTML)

						// Add class / Check input / change select value
						const isSelectedValue = paramValue == optionValueTextHandlize
						optionValueEl.classList.toggle('mw-option__value--selected', isSelectedValue)

						inputEl.checked = isSelectedValue

						if (isSelectedValue) {
							inputEl.dispatchEvent(new Event('change', { bubbles: true }))
							inputEl.dispatchEvent(new Event('click', { bubbles: true }))
						}
					}
				})
			}

			// Handle based on type: select
			if (optionRowEl.classList.contains('mw-option--type-select')) {
				const selectEl = optionRowEl.querySelector('select')
				if (!selectEl) return

				const selectOptionEls = selectEl.querySelectorAll('option')

				selectOptionEls.forEach((selectOptionEl) => {
					selectOptionEl.removeAttribute('data-hidden')
					selectOptionEl.removeAttribute('hidden')
					selectOptionEl.removeAttribute('disabled')
					selectOptionEl.classList.remove('mw-hidden')

					const optionName = selectOptionEl.innerHTML
					let optionValueHandlize = clearOptionsName(optionName)

					if (handlizedLabel == 'shaft-length') {
						const firstCharacter = optionName.trim().charAt(0)

						if (firstCharacter == '-') optionValueHandlize = `minus_${optionValueHandlize}`
						if (firstCharacter == '+') optionValueHandlize = `plus_${optionValueHandlize}`
					}

					if (paramValue == optionValueHandlize) {
						selectEl.value = selectOptionEl.value
						selectEl.dispatchEvent(new Event('change', { bubbles: true }))
					}
				})
			}
		})
	}

	for (let index = 0; index < 24; index++) {
		if (isLoaded) return

		const timeDelay = index * 500

		debounce(() => {
			preselectCustomizedProductsOptions()
		}, timeDelay)()
	}
})

/**************************************
 * [FUNCTION]: update dropdown items
 ***************************************/

document.addEventListener('DOMContentLoaded', function () {
	function variantSelectorEvent() {
		const variantSelectors = document.querySelectorAll('.t4s-swatch__list .t4s-dropdown__content')
		if (!variantSelectors) return

		variantSelectors.forEach((variantSelector) => {
			let attempts = 0
			const interval = setInterval(() => {
				// Check our condition
				if (!document.querySelector('.is--selected.is--disabled')) {
					clearInterval(interval)

					return
				}
				// Try to click the available item
				variantSelector.querySelector('.t4s-swatch__item.is--available')?.click()

				// Clear the interval after 10 attempts
				attempts++
				if (attempts >= 10) {
					clearInterval(interval)
				}
			}, 500)
		})
	}

	variantSelectorEvent()

	const swatchItems = document.querySelectorAll('[data-swatch-item]')
	const dropdownItems = document.querySelectorAll('[data-dropdown-item]')
	swatchItems.forEach((item) => {
		item.addEventListener('click', function (e) {
			setTimeout(() => {
				//Prevent change when item is out of stock
				if (item.classList.contains('is--soldout')) return
				const url = new URL(window.location.href)
				const variantId = url.searchParams.get('variant')
				// remove 'shopify-section-' from the section id
				const sectionId = document.querySelector('.t4s-section-main-product').id.slice(16)

				const param = variantId ? `&section_id=${sectionId}` : `?section_id=${sectionId}`

				fetch(url + param)
					.then((response) => response.text())
					.then((responseText) => {
						const parser = new DOMParser()
						const html = parser.parseFromString(responseText, 'text/html')

						// For each swatch item, find the corresponding one in the parsed HTML and replace class and innerText
						swatchItems.forEach((item) => {
							const matchingItemInResponse = html.querySelector(
								`[data-swatch-item][data-value="${item.getAttribute('data-value')}"]`
							)

							if (matchingItemInResponse) {
								item.className = item.classList.contains('is--soldout')
									? matchingItemInResponse.className + ' is--soldout'
									: matchingItemInResponse.className
								item.innerHTML = matchingItemInResponse.innerHTML
							}
						})
					})
			}, 200)
		})
	})

	dropdownItems.forEach((item) => {
		item.addEventListener('click', function (e) {
			setTimeout(() => {
				const url = new URL(window.location.href)
				const variantId = url.searchParams.get('variant')
				// remove 'shopify-section-' from the section id
				const sectionId = document.querySelector('.t4s-section-main-product').id.slice(16)

				const param = variantId ? `&section_id=${sectionId}` : `?section_id=${sectionId}`

				fetch(url + param)
					.then((response) => response.text())
					.then((responseText) => {
						const parser = new DOMParser()
						const html = parser.parseFromString(responseText, 'text/html')

						// For each swatch item, find the corresponding one in the parsed HTML and replace class and innerText
						swatchItems.forEach((item) => {
							const matchingItemInResponse = html.querySelector(
								`[data-swatch-item][data-value="${item.getAttribute('data-value')}"]`
							)

							if (matchingItemInResponse) {
								item.className = matchingItemInResponse.className
								item.innerHTML = matchingItemInResponse.innerHTML
							}
						})
					})
			}, 200)
		})
	})
})

/**************************************
 * [FUNCTION]: PRODUCT UTILITIES (GALLERY LOAD MORE & REVIEW MOVE)
 ***************************************/
function initMainProductUtilities() {
	function moveReviews() {
		const reviews = document.querySelector('.t4s-product__price-review + .shopify-app-block')
		if (!reviews) return

		const desktopTarget = document.querySelector('.t4s-product__info-container .product__prices')
		const mobileTarget = document.querySelector('.product__mobile-only .product__prices')
		const isDesktop = window.innerWidth > 767

		const desktopCanMove =
			isDesktop && desktopTarget && !desktopTarget.nextElementSibling?.classList.contains('shopify-app-block')
		const mobileCanMove =
			!isDesktop && mobileTarget && !mobileTarget.nextElementSibling?.classList.contains('shopify-app-block')

		if (desktopCanMove) {
			desktopTarget.insertAdjacentElement('afterend', reviews)
		} else if (mobileCanMove) {
			mobileTarget.insertAdjacentElement('afterend', reviews)
		}
	}

	function handlePopupClick(e) {
		const popup = document.querySelector('.pswp')
		if (popup && !popup.contains(e.target) && popup.classList.contains('pswp--open')) {
			popup.className = 'pswp pswp__t4s'
		}
	}

	function setupLoadMore() {
		const loadMoreButton = document.querySelector('.load-more-button')
		const gallery = document.querySelector('[data-t4s-gallery]')
		if (!loadMoreButton || !gallery) return

		loadMoreButton.addEventListener('click', function () {
			const expended = 'gallery--expended'
			const span = loadMoreButton.querySelector('span')
			const isExpended = gallery.classList.contains(expended)

			if (isExpended) {
				gallery.classList.remove(expended)
				if (span) span.textContent = 'Show More'

				// Scroll higher so the user is not stranded.
				window.scrollTo({
					top: loadMoreButton.offsetTop - 350,
					left: 0,
					behavior: 'instant',
				})
			} else {
				gallery.classList.add(expended)
				if (span) span.textContent = 'Show Less'
			}
		})
	}

	function bindEvents() {
		document.addEventListener('DOMContentLoaded', function () {
			moveReviews()
			setupLoadMore()
		})
		window.addEventListener('resize', moveReviews)
		document.addEventListener('click', handlePopupClick)
	}

	bindEvents()
}

initMainProductUtilities()

